import { useState, useEffect } from "react";
import COLORS from "../config/colors";
import {
  updateProjectPlans, updateProjectPermit,
  fetchInspections, createInspection, updateInspection,
  uploadFile,
} from "../lib/supabase";

const PLAN_STATUSES = [
  { value: "not_needed", label: "Not Needed", color: COLORS.stone },
  { value: "pending", label: "Pending", color: COLORS.honey },
  { value: "in_progress", label: "In Progress", color: COLORS.blue },
  { value: "submitted", label: "Submitted", color: COLORS.honey },
  { value: "revision_requested", label: "Revision Needed", color: COLORS.red },
  { value: "approved", label: "Approved", color: COLORS.green },
];

const PERMIT_STATUSES = [
  { value: "not_needed", label: "Not Needed", color: COLORS.stone },
  { value: "pending", label: "Pending", color: COLORS.honey },
  { value: "applied", label: "Applied", color: COLORS.blue },
  { value: "under_review", label: "Under Review", color: COLORS.honey },
  { value: "approved", label: "Approved", color: COLORS.green },
  { value: "denied", label: "Denied", color: COLORS.red },
];

const INSPECTION_TYPES = [
  "footing", "framing", "rough_in", "insulation", "final", "other"
];

function StatusPill({ status, statuses }) {
  const cfg = statuses.find(s => s.value === status) || statuses[0];
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 12px", borderRadius: 20,
      background: `${cfg.color}15`, color: cfg.color,
      fontSize: 12, fontWeight: 600, fontFamily: "'DM Sans', sans-serif",
    }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

export default function PermitTracker({ project, onProjectUpdate, styles }) {
  const [inspections, setInspections] = useState([]);
  const [showAddInspection, setShowAddInspection] = useState(false);
  const [newInspection, setNewInspection] = useState({ inspection_type: "footing", scheduled_date: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project?.id) {
      fetchInspections(project.id).then(setInspections);
    }
  }, [project?.id]);

  if (!project) return null;

  const plans = {
    required: project.plans_required || false,
    status: project.plans_status || "not_needed",
    engineer: project.engineer_name || "",
    notes: project.plans_notes || "",
    fileUrl: project.plans_file_url || null,
  };

  const permit = {
    required: project.permit_required || false,
    status: project.permit_status || "not_needed",
    number: project.permit_number || "",
    jurisdiction: project.permit_jurisdiction || "",
    fee: project.permit_fee || "",
    notes: project.permit_notes || "",
    fileUrl: project.permit_file_url || null,
  };

  const handlePlansUpdate = async (updates) => {
    setSaving(true);
    const updated = await updateProjectPlans(project.id, updates);
    if (updated) onProjectUpdate(updated);
    setSaving(false);
  };

  const handlePermitUpdate = async (updates) => {
    setSaving(true);
    const updated = await updateProjectPermit(project.id, updates);
    if (updated) onProjectUpdate(updated);
    setSaving(false);
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const path = `${type}/${project.job_code || project.id}/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
    const url = await uploadFile('documents', path, file);
    if (url) {
      if (type === 'plans') await handlePlansUpdate({ plans_file_url: url });
      else await handlePermitUpdate({ permit_file_url: url });
    }
  };

  const handleAddInspection = async () => {
    if (!newInspection.scheduled_date) return;
    setSaving(true);
    const created = await createInspection({
      project_id: project.id,
      job_code: project.job_code,
      inspection_type: newInspection.inspection_type,
      scheduled_date: newInspection.scheduled_date,
      status: "scheduled",
    });
    if (created) setInspections(prev => [...prev, created]);
    setShowAddInspection(false);
    setNewInspection({ inspection_type: "footing", scheduled_date: "" });
    setSaving(false);
  };

  const handleInspectionUpdate = async (id, updates) => {
    const updated = await updateInspection(id, updates);
    if (updated) setInspections(prev => prev.map(i => i.id === id ? updated : i));
  };

  const sty = {
    section: { padding: 16, background: COLORS.white, borderRadius: 12, border: "1px solid rgba(155,142,130,0.15)", marginBottom: 12 },
    sectionTitle: { fontSize: 14, fontWeight: 700, color: COLORS.bark, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 },
    row: { display: "flex", gap: 8, marginBottom: 8, alignItems: "center" },
    label: { fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: COLORS.stone, marginBottom: 4 },
    input: { ...styles.input, padding: "8px 12px", fontSize: 13 },
    smallBtn: { padding: "6px 14px", borderRadius: 8, border: "none", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "'DM Sans', sans-serif" },
  };

  return (
    <div>
      {/* ── DESIGN / PLANS ── */}
      <div style={sty.section}>
        <div style={sty.sectionTitle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.blue} strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          Design / Plans
          <StatusPill status={plans.status} statuses={PLAN_STATUSES} />
        </div>

        <div style={sty.row}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={plans.required}
              onChange={e => handlePlansUpdate({
                plans_required: e.target.checked,
                plans_status: e.target.checked ? "pending" : "not_needed",
              })} />
            Engineered plans required
          </label>
        </div>

        {plans.required && (
          <>
            <div style={sty.row}>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Status</div>
                <select value={plans.status}
                  onChange={e => handlePlansUpdate({
                    plans_status: e.target.value,
                    ...(e.target.value === "approved" ? { plans_approved_at: new Date().toISOString() } : {}),
                    ...(e.target.value === "submitted" ? { plans_submitted_at: new Date().toISOString() } : {}),
                  })}
                  style={{ ...sty.input, background: COLORS.cream }}>
                  {PLAN_STATUSES.filter(s => s.value !== "not_needed").map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Engineer</div>
                <input value={plans.engineer} placeholder="Engineer name"
                  onBlur={e => handlePlansUpdate({ engineer_name: e.target.value })}
                  onChange={e => {/* controlled locally until blur */}}
                  defaultValue={plans.engineer}
                  style={sty.input} />
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={sty.label}>Notes</div>
              <textarea defaultValue={plans.notes} placeholder="Plan notes, revision details..."
                onBlur={e => handlePlansUpdate({ plans_notes: e.target.value })}
                style={{ ...sty.input, minHeight: 48, resize: "vertical" }} />
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ ...sty.smallBtn, background: COLORS.cream, color: COLORS.bark, border: `1px solid rgba(155,142,130,0.2)`, cursor: "pointer" }}>
                Upload Plans
                <input type="file" accept=".pdf,image/*" onChange={e => handleFileUpload(e, 'plans')} style={{ display: "none" }} />
              </label>
              {plans.fileUrl && (
                <a href={plans.fileUrl} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: COLORS.blue, fontWeight: 600, textDecoration: "none" }}>
                  View Plans
                </a>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── PERMITS ── */}
      <div style={sty.section}>
        <div style={sty.sectionTitle}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.honey} strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          Permit
          <StatusPill status={permit.status} statuses={PERMIT_STATUSES} />
        </div>

        <div style={sty.row}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={permit.required}
              onChange={e => handlePermitUpdate({
                permit_required: e.target.checked,
                permit_status: e.target.checked ? "pending" : "not_needed",
              })} />
            Building permit required
          </label>
        </div>

        {permit.required && (
          <>
            <div style={sty.row}>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Status</div>
                <select value={permit.status}
                  onChange={e => handlePermitUpdate({
                    permit_status: e.target.value,
                    ...(e.target.value === "approved" ? { permit_approved_at: new Date().toISOString() } : {}),
                    ...(e.target.value === "applied" ? { permit_applied_at: new Date().toISOString() } : {}),
                  })}
                  style={{ ...sty.input, background: COLORS.cream }}>
                  {PERMIT_STATUSES.filter(s => s.value !== "not_needed").map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Permit #</div>
                <input defaultValue={permit.number} placeholder="BLD-2026-XXXX"
                  onBlur={e => handlePermitUpdate({ permit_number: e.target.value })}
                  style={sty.input} />
              </div>
            </div>

            <div style={sty.row}>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Jurisdiction</div>
                <input defaultValue={permit.jurisdiction} placeholder="Ada County"
                  onBlur={e => handlePermitUpdate({ permit_jurisdiction: e.target.value })}
                  style={sty.input} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Permit Fee</div>
                <input type="number" defaultValue={permit.fee} placeholder="0"
                  onBlur={e => handlePermitUpdate({ permit_fee: Number(e.target.value) || 0 })}
                  style={sty.input} />
              </div>
            </div>

            <div style={{ marginBottom: 8 }}>
              <div style={sty.label}>Notes</div>
              <textarea defaultValue={permit.notes} placeholder="Application details, contact info..."
                onBlur={e => handlePermitUpdate({ permit_notes: e.target.value })}
                style={{ ...sty.input, minHeight: 48, resize: "vertical" }} />
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <label style={{ ...sty.smallBtn, background: COLORS.cream, color: COLORS.bark, border: `1px solid rgba(155,142,130,0.2)`, cursor: "pointer" }}>
                Upload Permit
                <input type="file" accept=".pdf,image/*" onChange={e => handleFileUpload(e, 'permits')} style={{ display: "none" }} />
              </label>
              {permit.fileUrl && (
                <a href={permit.fileUrl} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 12, color: COLORS.blue, fontWeight: 600, textDecoration: "none" }}>
                  View Permit
                </a>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── INSPECTIONS ── */}
      {permit.required && (
        <div style={sty.section}>
          <div style={sty.sectionTitle}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.green} strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
            Inspections
            <span style={{ fontSize: 11, color: COLORS.stone, fontWeight: 400, marginLeft: "auto" }}>
              {inspections.filter(i => i.status === "passed").length}/{inspections.length} passed
            </span>
          </div>

          {inspections.length === 0 && !showAddInspection && (
            <div style={{ fontSize: 13, color: COLORS.stone, marginBottom: 8 }}>
              No inspections scheduled yet.
            </div>
          )}

          {inspections.map(insp => (
            <div key={insp.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", borderBottom: "1px solid rgba(155,142,130,0.08)" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.bark, textTransform: "capitalize" }}>
                  {insp.inspection_type.replace("_", " ")}
                </div>
                <div style={{ fontSize: 11, color: COLORS.stone }}>
                  {insp.scheduled_date}{insp.inspector_name ? ` · ${insp.inspector_name}` : ""}
                </div>
                {insp.result_notes && <div style={{ fontSize: 11, color: COLORS.stone, marginTop: 2 }}>{insp.result_notes}</div>}
                {insp.corrections_needed && <div style={{ fontSize: 11, color: COLORS.red, marginTop: 2 }}>Corrections: {insp.corrections_needed}</div>}
              </div>
              <select value={insp.status}
                onChange={e => handleInspectionUpdate(insp.id, {
                  status: e.target.value,
                  ...(e.target.value === "passed" || e.target.value === "failed" ? { completed_date: new Date().toISOString().split("T")[0] } : {}),
                })}
                style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(155,142,130,0.2)", fontSize: 11, fontFamily: "'DM Sans', sans-serif",
                  color: insp.status === "passed" ? COLORS.green : insp.status === "failed" ? COLORS.red : COLORS.bark,
                  fontWeight: 600,
                }}>
                <option value="scheduled">Scheduled</option>
                <option value="passed">Passed</option>
                <option value="failed">Failed</option>
                <option value="rescheduled">Rescheduled</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          ))}

          {showAddInspection ? (
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Type</div>
                <select value={newInspection.inspection_type}
                  onChange={e => setNewInspection(p => ({ ...p, inspection_type: e.target.value }))}
                  style={{ ...sty.input, background: COLORS.cream, textTransform: "capitalize" }}>
                  {INSPECTION_TYPES.map(t => <option key={t} value={t}>{t.replace("_", " ")}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <div style={sty.label}>Date</div>
                <input type="date" value={newInspection.scheduled_date}
                  onChange={e => setNewInspection(p => ({ ...p, scheduled_date: e.target.value }))}
                  style={sty.input} />
              </div>
              <button onClick={handleAddInspection} disabled={saving}
                style={{ ...sty.smallBtn, background: COLORS.green, color: COLORS.white }}>
                Add
              </button>
              <button onClick={() => setShowAddInspection(false)}
                style={{ ...sty.smallBtn, background: "transparent", color: COLORS.stone }}>
                Cancel
              </button>
            </div>
          ) : (
            <button onClick={() => setShowAddInspection(true)}
              style={{ ...sty.smallBtn, background: "none", border: `1px dashed ${COLORS.green}`, color: COLORS.green, width: "100%", marginTop: 8 }}>
              + Schedule Inspection
            </button>
          )}
        </div>
      )}
    </div>
  );
}
